import React, { FC } from 'react';

import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';

import {
  StyledPlaySmallIcon,
  StyledRepeatIcon,
  Wrapper,
} from '~/components/Card/components/CardActionBlocks/styles';
import CardTestResults from '~/components/Card/components/CardTestResults/CardTestResults';
import { ShareType } from '~/components/ShareModal/ShareModal';
import CustomButton from '~/components/UI/CustomButton/CustomButton';
import { useTenantContext } from '~/context/TenantProvider';
import { GET_COURSE_COMPLETION_BY_HASH } from '~/graphql/courses';
import useIsSsku from '~/hooks/tenant/useIsSsku';
import useDownloadFile from '~/hooks/useDownloadFile';
import useIsSSR from '~/hooks/useIsSSR';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { generateImageKitUrl } from '~/modules/imageKit';
import { COURSE_PAGE_EVENTS } from '~/pages/courses/const';
import { buildCourseProgressRoute, buildCourseRoute, buildCoursesShareRoute } from '~/routes';
import { CssUtilityType } from '~/styled';
import { Course, CourseStatus, Maybe, Query, QueryGetCourseStatusByHashArgs } from '~/types';
import { generateCertificateName, getCourseCertificatePDFUrl } from '~/utils/certificateUtils';

interface ICardCourseActionBlock {
  wrapperCss?: CssUtilityType;
  data?: Maybe<Course>;
}

const CardCourseActionBlock: FC<ICardCourseActionBlock> = ({ data, wrapperCss }) => {
  const { t } = useTenantTranslation();
  const isSSR = useIsSSR();
  const hash = data?.hash;
  const { download, loading: downloadingCertificate } = useDownloadFile();
  const { tenant } = useTenantContext();
  const isSsku = useIsSsku();

  const { data: dataByHash } = useQuery<
    Pick<Query, 'getCourseStatusByHash'>,
    QueryGetCourseStatusByHashArgs
  >(GET_COURSE_COMPLETION_BY_HASH, {
    variables: {
      hash: hash || '',
    },
    skip: !hash || isSSR,
  });

  const linkedinModalProps = {
    entityName: data?.name,
    resultHash: data?.hash,
    passDate: dataByHash?.getCourseStatusByHash?.completed_at,
    certUrl: `${window?.location?.origin}${buildCoursesShareRoute(hash)}`,
  };

  const shareModalProps = {
    resultHash: hash,
    entityName: data?.name,
    type: ShareType.Course,
  };

  return (
    <Wrapper optionalStyles={wrapperCss}>
      {data?.status === CourseStatus.Completed ? (
        <CardTestResults
          downloadButtonCaption={
            isSsku ? t('common.certificate') : t('page.pathway.cpdCertificate')
          }
          handleCertDownload={() =>
            download(
              generateCertificateName(tenant?.title, data?.name),
              generateImageKitUrl({
                path: getCourseCertificatePDFUrl(hash, tenant?.name),
                transformation: undefined,
              }),
            )
          }
          certDownloading={downloadingCertificate}
          linkedinModalProps={linkedinModalProps}
          shareModalProps={shareModalProps}
          gtmEvents={COURSE_PAGE_EVENTS}
        />
      ) : (
        <div />
      )}
      {data?.status === CourseStatus.Started && (
        <Link data-testid="courseLink" to={buildCourseProgressRoute(data?.slug)}>
          <CustomButton data-testid="continueWatchingBtn">
            <StyledPlaySmallIcon data-testid="play-icon" className="playIcon" />
            {t('page.discover.continueWatching')}
          </CustomButton>
        </Link>
      )}
      {data?.status === CourseStatus.Completed && (
        <Link data-testid="courseLink" to={buildCourseRoute(data?.slug)}>
          <CustomButton data-testid="rewatch">
            <StyledRepeatIcon data-testid="repeat-icon" className="repeatIcon" />
            {t('page.discover.rewatch')}
          </CustomButton>
        </Link>
      )}
    </Wrapper>
  );
};

export default CardCourseActionBlock;
